import type { WorkspaceDataStatus } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { DataFreshnessBar } from "./DataFreshnessBar";

interface ShopDataHistoryProps {
  shop: WorkspaceDataStatus["shops"][number];
  shopName: string;
  unavailable?: boolean;
  /** Vendor (1P) shop: no Seller Central sales until Vendor Central lands (#679). */
  vendor?: boolean;
}

export function ShopDataHistory({
  shop,
  shopName,
  unavailable = false,
  vendor = false,
}: ShopDataHistoryProps) {
  const { t, i18n } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const primary = "ads";
  const secondary = "sales";
  const name = (kind: "ads" | "sales") =>
    t(kind === "ads" ? "dataStatus.adsReports" : "dataStatus.salesReports");
  const source = shop.sources.find((item) => item.source === primary);
  const daysFor = (kind: "ads" | "sales") => {
    const item = shop.sources.find((entry) => entry.source === kind);
    const hidden =
      unavailable ||
      item?.state === "action_required" ||
      item?.state === "paused" ||
      item?.state === "unavailable";
    return (item?.reportQuality?.days.slice(-60) ?? []).map((day) =>
      hidden ? { ...day, status: "unknown" as const, updatedAt: null } : day,
    );
  };
  const primaryDays = daysFor(primary);
  // A vendor's Sales row is not pending data, so it neither draws nor gates the summary.
  const secondaryDays = vendor ? [] : daysFor(secondary);
  const displayDays = primaryDays.length
    ? primaryDays
    : secondaryDays.map((day) => ({ ...day, status: "unknown" as const, updatedAt: null }));
  const blocked =
    unavailable ||
    source?.state === "action_required" ||
    source?.state === "paused" ||
    source?.state === "unavailable";
  const complete =
    primaryDays.length === 60 &&
    (vendor || secondaryDays.length === 60) &&
    [...primaryDays, ...secondaryDays].every((day) => day.status === "synced");
  const pending = [...primaryDays, ...secondaryDays].some(
    (day) => day.status === "pending" || day.status === "attention",
  );
  const labelKey = blocked
    ? `dataStatus.sourceShort.${unavailable ? "unavailable" : source?.state}`
    : complete
      ? "dataStatus.shopReady"
      : pending
        ? "dataStatus.shopPending"
        : "dataStatus.shopUnknown";
  const updated = [...primaryDays].reverse().find((day) => day.status === "synced")?.updatedAt;
  return (
    <div className="flex max-w-[740px] flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">
            {vendor ? name(primary) : `${name(primary)} / ${name(secondary)}`}
          </span>
        </div>
        <span className="text-muted-foreground">
          {t(labelKey)}
          {updated && !blocked
            ? ` · ${t("dataStatus.updatedAt", { time: new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(updated)) })}`
            : ""}
        </span>
      </div>
      <DataFreshnessBar
        days={displayDays}
        secondaryDays={vendor ? undefined : secondaryDays}
        secondaryName={vendor ? undefined : name(secondary)}
        shopName={shopName}
        sourceName={name(primary)}
      />
      {vendor ? (
        <p data-slot="vendor-sales-pending" className="text-xs text-muted-foreground">
          {name(secondary)} · {tCommon("vendorData.salesPending")}
        </p>
      ) : null}
    </div>
  );
}
